import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { QuestionCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Components
import BooleanIcon from '../../utils/BooleanIcon';

// Actions
import { get_all_self_settings, self_setting_delete } from '../../../redux/actions/selfSettingsActions';

// Utils
import { get_load_scope_desc } from '../../../utils/get_status_desc';

const SettingsTable = (props) => {
	const { messageApi, orgFilters, setIsModalOpen, setUpdate, setSettingToUpdate, pageSize, skip, setSkip } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const _ls = { ...localStorage };

	const { all_self_settings_loading, all_self_settings, self_setting_delete_loading } = useSelector(state => state.self_settings);

	const update = (record) => {
		setIsModalOpen(true);
		setUpdate(true);
		setSettingToUpdate(record);
	}

	const deleteSelfSetting = (record) => {
		const filters = { ...orgFilters, skip: skip, limit: pageSize };
		dispatch(self_setting_delete(messageApi, record._id.$oid, filters));
	}

	const columns = [
		...(orgFilters.organization === ''
			? [
					{
						title: t('filters.organization'),
						dataIndex: 'organization',
						key: 'organization',
						render: (organization) => (
							organization
								?	<Typography.Text copyable={{ text: organization?._id?.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
										{organization?.name}
									</Typography.Text>
							: '-'
						)
					}
				]
			: []
		),
		...(orgFilters.workspace === ''
			? [
					{
						title: t('filters.workspace'),
						dataIndex: 'workspace',
						key: 'workspace',
						render: (workspace) => (
							workspace
								?	<Typography.Text copyable={{ text: workspace?._id?.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
										{workspace?.name}
									</Typography.Text>
							: '-'
						)
					}
				]
			: []
		),
		...(orgFilters.module === ''
			? [
					{
						title: t('filters.module'),
						dataIndex: 'module',
						key: 'module',
						render: (module) => (
							module
								?	<Typography.Text copyable={{ text: module?._id?.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
										{module?.name}
									</Typography.Text>
							: '-'
						)
					}
				]
			: []
		),
		{
			title: t('routesTable.headers.name'),
			dataIndex: 'name',
			key: 'name',
			render: (_, record) => (
				<Typography.Text copyable={{ text: record._id.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
					{record.name}
				</Typography.Text>
			)
		},
		{
			title: t('loadFileInput.description'),
			dataIndex: 'description',
			key: 'description'
		},
		{
			title: t('schemasForm.inputs.scope'),
			dataIndex: 'scope',
			key: 'scope',
			render: (text) => (
				<Typography.Text>{get_load_scope_desc(text)}</Typography.Text>
			)
		},
		{
			title: t('settingsForm.inputs.dailyFrequency'),
			dataIndex: 'daily_frequency',
			key: 'daily_frequency',
			render: (text) => (
				<BooleanIcon value={text} />
			)
		},
		{
			title: t('settingsForm.inputs.weeklyFrequency'),
			dataIndex: 'weekly_frequency',
			key: 'weekly_frequency',
			render: (text) => (
				<BooleanIcon value={text} />
			)
		},
		{
			title: t('loadStatus.statusTable.date'),
			dataIndex: ['date', '$date'],
			key: 'date',
			render: (text) => (
				<Typography.Text>{new Date(text).toLocaleString(_ls.i18nextLng, { hour12: true })}</Typography.Text>
			)
		},
		{
			title: t('routesTable.headers.action'),
			dataIndex: 'action',
			key: 'action',
			render: (_, record) => (
				<Space>
					<Tooltip title={t('settingsForm.edit.title').split(' ')[0]}>
						<Button
							type='primary'
							shape='circle'
							size='large'
							icon={<i className='bi bi-pencil-fill'></i>}
							onClick={() => update(record)}
						/>
					</Tooltip>
					<Popconfirm
						title={t('settingsForm.delete.popconfirm.title')}
						description={t('settingsForm.delete.popconfirm.description')}
						icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
						onConfirm={() => deleteSelfSetting(record)}
						okButtonProps={{
							loading: self_setting_delete_loading
						}}
						okText={t('settingsForm.delete.popconfirm.confirm')}
						cancelText={t('settingsForm.delete.popconfirm.cancel')}
					>
						<Button
							type='primary'
							shape='circle'
							size='large'
							danger
							icon={<span className='material-symbols-outlined'>delete</span>}
						/>
					</Popconfirm>
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pageSize;
		setSkip(skip);

		const settingFilters = { ...orgFilters, skip: skip, limit: pageSize };
		dispatch(get_all_self_settings(settingFilters));
	};

	return (
		<div className='mt-5'>
			<Spin spinning={all_self_settings_loading} tip={t('setting.loading')} size='large'>
				<Table
					columns={columns}
					dataSource={all_self_settings.settings}
					onChange={handleTableChange}
					pagination={{
						pageSize: pageSize,
						total: all_self_settings.count,
						showTotal: (total, range) => `${range[0]} - ${range[1]} ${t('pagination.of')} ${total}`
					}}
					scroll={{
						x: 1000
					}}
				/>
			</Spin>
		</div>
	)
}

SettingsTable.propTypes = {
	messageApi: PropTypes.object.isRequired,
	orgFilters: PropTypes.object.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	setUpdate: PropTypes.func.isRequired,
	setSettingToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default SettingsTable;
